// Helper functions for version management
const getCurrentVersion = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

const getCurrentDate = () => {
  return getCurrentVersion(); // Same format for now
};

// Changes tracking data structure for documentation releases
export const changesData = {
  // Current release being prepared
  currentRelease: {
    version: getCurrentVersion(),
    date: getCurrentDate(),
    status: "draft", // draft, released
    changes: [
      // Patient Form - Added new race field
      {
        id: `change-${Date.now()}`,
        type: "added",
        documentId: "patient-edit",
        section: "Fields Specification",
        title: "Added race field to Patient Form",
        description: "New race/ethnicity selector with 8 predefined options: Non-Hispanic White/Caucasian, Hispanic/Latino, African American Black, Asian, American Indian or Alaska Native, Native Hawaiian or Other Pacific Islander, Middle Eastern or North African, Multiracial or none of the above",
        content: "race field",
        timestamp: new Date().toISOString()
      }
    ]
  },

  // Released versions history
  releases: [
    // Previous releases will be moved here when new versions are deployed
  ]
};

// Helper function to get changes for a specific document
export const getDocumentChanges = (documentId, releaseVersion = null) => {
  if (releaseVersion) {
    const release = changesData.releases.find(r => r.version === releaseVersion);
    return release ? release.changes.filter(c => c.documentId === documentId) : [];
  }

  // Return current release changes
  return changesData.currentRelease.changes.filter(c => c.documentId === documentId);
};

// Helper function to get all changed documents
export const getChangedDocuments = (releaseVersion = null) => {
  const changes = releaseVersion ?
    changesData.releases.find(r => r.version === releaseVersion)?.changes || [] :
    changesData.currentRelease.changes;

  const changedDocs = new Set(changes.map(c => c.documentId));
  return Array.from(changedDocs);
};

// Helper function to check if document has changes
export const hasDocumentChanges = (documentId, releaseVersion = null) => {
  const changes = getDocumentChanges(documentId, releaseVersion);
  return changes.length > 0;
};

// Helper function to get change counts by type
export const getChangesCounts = (documentId = null, releaseVersion = null) => {
  const changes = documentId ?
    getDocumentChanges(documentId, releaseVersion) :
    (releaseVersion ?
      changesData.releases.find(r => r.version === releaseVersion)?.changes || [] :
      changesData.currentRelease.changes);

  return {
    added: changes.filter(c => c.type === 'added').length,
    modified: changes.filter(c => c.type === 'modified').length,
    deleted: changes.filter(c => c.type === 'deleted').length,
    newSection: changes.filter(c => c.type === 'new-section').length,
    total: changes.length
  };
};

// Functions for managing changes (for future use)
export const addChange = (change) => {
  const newChange = {
    id: `change-${Date.now()}`,
    ...change,
    timestamp: new Date().toISOString()
  };
  changesData.currentRelease.changes.push(newChange);
  return newChange;
};

export const removeChange = (changeId) => {
  changesData.currentRelease.changes = changesData.currentRelease.changes.filter(
    change => change.id !== changeId
  );
};

export const releaseCurrentVersion = () => {
  if (changesData.currentRelease.changes.length === 0) {
    return false; // No changes to release
  }

  // Move current release to releases history
  const release = {
    ...changesData.currentRelease,
    status: "released",
    releasedAt: new Date().toISOString()
  };

  changesData.releases.unshift(release); // Add to beginning of array

  // Create new current release
  changesData.currentRelease = {
    version: getCurrentVersion(),
    date: getCurrentDate(),
    status: "draft",
    changes: []
  };

  return release;
};

// Enhanced color scheme with better contrast
export const changeColors = {
  added: {
    color: '#059669', // Emerald-600
    bg: 'rgba(5, 150, 105, 0.15)',
    border: 'rgba(5, 150, 105, 0.4)'
  },
  modified: {
    color: '#D97706', // Amber-600
    bg: 'rgba(217, 119, 6, 0.15)',
    border: 'rgba(217, 119, 6, 0.4)'
  },
  deleted: {
    color: '#DC2626', // Red-600
    bg: 'rgba(220, 38, 38, 0.15)',
    border: 'rgba(220, 38, 38, 0.4)'
  },
  newSection: {
    color: '#2563EB', // Blue-600
    bg: 'rgba(37, 99, 235, 0.15)',
    border: 'rgba(37, 99, 235, 0.4)'
  }
};
